import React, { useState } from "react";
import { Plus, CheckCircle } from "react-feather";
import { useTheme } from "../../theme/ThemeContext";
import { isInPeriod } from "../../utils/period";
import { REPEAT_FREQUENCIES, scheduleLabel } from "../../utils/billFrequency";
import { INVESTMENT_TYPES } from "../../utils/investmentTypes";
import { PlannedBudgets, PlannedBudgetSheet } from "../../analysis/Budgets";
import { estimatedValue } from "../../utils/investmentValue";
import { payoffText } from "../../utils/loans";
import { INCOME_TYPES } from "../../utils/expenseTypes";
import { bankAccountsOf, sourceRequired, receivedIntoValue, receivedIntoId, paidFromFields } from "../../utils/moneySources";
import MoneySourceSelect from "../../components/MoneySourceSelect";
import { Card, SectionTitle, Sheet, Button, Field, Empty, Chip, ChipRow, LinkButton, StatusPill, useInputStyle, useMoney, formatDate, todayIso } from "../ui";

// What each kind of holding is, and the types offered for it.
const CATEGORIES = [
  { id: "account", label: "Account", types: ["Savings account", "Current account", "Salary account", "Wallet", "Cash", "Other"] },
  { id: "investment", label: "Investment", types: INVESTMENT_TYPES },
  { id: "other", label: "Other", types: ["Chit fund", "Property", "Lent money", "Other"] },
];
const categoryOf = (id) => CATEGORIES.find((c) => c.id === id) || CATEGORIES[0];


const numberOrNull = (s) => {
  if (String(s).trim() === "") return null;
  const n = parseFloat(s);
  return Number.isNaN(n) ? NaN : n;
};

// "Every N days / months / years", or doesn't repeat.
const FrequencyFields = ({ label, form, setForm }) => {
  const input = useInputStyle();
  return (
    <div style={{ display: "flex", gap: 10 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Field label={label}>
          <select style={input} value={form.repeatUnit} onChange={(e) => setForm((f) => ({ ...f, repeatUnit: e.target.value }))}>
            {REPEAT_FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </Field>
      </div>
      {form.repeatUnit !== "once" && (
        <div style={{ width: 90 }}>
          <Field label="Every">
            <input style={input} type="number" min="1" max="365" inputMode="numeric" value={form.repeatEvery} onChange={(e) => setForm((f) => ({ ...f, repeatEvery: e.target.value }))} />
          </Field>
        </div>
      )}
    </div>
  );
};

const repeatEveryOf = (form) => (form.repeatUnit === "once" ? 1 : Math.min(365, Math.max(1, parseInt(form.repeatEvery, 10) || 1)));

// ---- accounts & investments ---------------------------------------------------------

// One form for all three kinds; the fields change with the kind:
//   Account:    name, type, current value
//   Investment: name, type, frequency, current value, return % (optional)
//   Other:      the same as an investment, plus a description
// A repeating investment also needs the instalment and its next date; each
// instalment then shows up as an expense (Pending until paid).
const AssetSheet = ({ asset, data, onClose }) => {
  const input = useInputStyle();
  const { tokens } = useTheme();
  const editing = !!asset;
  const [form, setForm] = useState(() => ({
    category: asset ? asset.category || "account" : "account",
    name: asset ? asset.name : "",
    type: asset ? asset.type : CATEGORIES[0].types[0],
    // An investment saved without a value is stored as 0; show that as empty again.
    value: asset && !(asset.value === 0 && asset.category !== "account") ? String(asset.value) : "",
    repeatUnit: asset && asset.repeatUnit ? asset.repeatUnit : "months",
    repeatEvery: asset && asset.repeatEvery ? String(asset.repeatEvery) : "1",
    amount: asset && asset.amount ? String(asset.amount) : "",
    startDate: asset && asset.startDate ? asset.startDate : todayIso(),
    returnRate: asset && asset.returnRate != null ? String(asset.returnRate) : "",
    description: asset ? asset.description || "" : "",
    last4: asset && asset.last4 ? asset.last4 : "",
  }));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const category = categoryOf(form.category);
  const isAccount = form.category === "account";
  const repeats = !isAccount && form.repeatUnit !== "once";
  // An older entry may have a type that isn't in today's list; keep it selectable.
  const types = category.types.includes(form.type) ? category.types : [form.type, ...category.types];

  const switchCategory = (id) =>
    setForm((f) => ({ ...f, category: id, type: categoryOf(id).types[0], repeatUnit: id === "account" ? "once" : f.repeatUnit === "once" && !editing ? "months" : f.repeatUnit }));

  const submit = async (e) => {
    e.preventDefault();
    // Optional for investments and "other" holdings; an account needs its balance.
    const value = isAccount ? parseFloat(form.value) : numberOrNull(form.value);
    const returnRate = numberOrNull(form.returnRate);
    const amount = repeats ? parseFloat(form.amount) : 0;
    if (!form.name.trim()) return setError("Give it a name.");
    // An account can be below zero (overdrawn); an investment can't.
    if (Number.isNaN(value) || (!isAccount && value < 0)) return setError(isAccount ? "Enter a valid current value." : "Enter a valid amount, or leave it empty.");
    if (repeats && (Number.isNaN(amount) || amount <= 0)) return setError("Enter how much goes in each time.");
    if (repeats && !form.startDate) return setError("Choose the date of the next instalment.");
    if (Number.isNaN(returnRate)) return setError("Enter the return as a number, or leave it empty.");
    if (isAccount && form.last4.trim() && !/^\d{4}$/.test(form.last4.trim())) return setError("Enter the last 4 digits of the account number, or leave it empty.");
    setSaving(true);
    setError("");
    try {
      const body = {
        name: form.name.trim(),
        type: form.type,
        value,
        category: form.category,
        repeatUnit: repeats ? form.repeatUnit : "once",
        repeatEvery: repeats ? repeatEveryOf(form) : 1,
        amount: repeats ? amount : 0,
        startDate: repeats ? form.startDate : null,
        returnRate: isAccount ? null : returnRate,
        description: form.category === "other" ? form.description.trim() : "",
        last4: isAccount ? form.last4.trim() || null : null,
      };
      if (editing) await data.update("assets", asset.id, body);
      else await data.create("assets", body);
      // Instalments become expense entries on the server, so pick those up too.
      data.reload();
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Sheet title={editing ? `Edit ${category.label.toLowerCase()}` : "Add Accounts & Investments"} onClose={onClose}>
      <form onSubmit={submit}>
        <ChipRow>
          {CATEGORIES.map((c) => (
            <Chip key={c.id} active={form.category === c.id} onClick={() => switchCategory(c.id)}>{c.label}</Chip>
          ))}
        </ChipRow>
        <div style={{ height: 8 }} />
        <Field label="Name">
          <input style={input} value={form.name} onChange={set("name")} placeholder={isAccount ? "e.g. SBI Savings" : form.category === "investment" ? "e.g. Axis Bluechip SIP" : "e.g. Family chit fund"} />
        </Field>
        <Field label="Type">
          <select style={input} value={form.type} onChange={set("type")}>
            {types.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>

        {!isAccount && (
          <>
            <FrequencyFields label="Investment frequency" form={form} setForm={setForm} />
            {repeats && (
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Field label="Each instalment (₹)"><input style={input} type="number" inputMode="decimal" value={form.amount} onChange={set("amount")} placeholder="0" /></Field>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Field label="Next instalment on"><input style={input} type="date" value={form.startDate} onChange={set("startDate")} /></Field>
                </div>
              </div>
            )}
          </>
        )}

        <Field label={isAccount ? "Current value (₹)" : "Invested so far (₹, optional)"}>
          <input style={input} type="number" inputMode="decimal" value={form.value} onChange={set("value")} placeholder={isAccount ? "0" : "What's already in it"} />
        </Field>
        {isAccount && (
          <Field label="Last 4 digits of the account number (optional)">
            <input style={input} inputMode="numeric" maxLength={4} value={form.last4} onChange={set("last4")} placeholder="e.g. 1234 - matches bank SMS to this account" />
          </Field>
        )}
        <p style={{ margin: "-6px 0 12px", fontSize: 12, color: tokens.textMuted }}>
          {isAccount
            ? "Today's balance. Expenses and money received from today on change it; past ones you add later don't, since this already includes them."
            : "Goes up by itself each time an instalment is marked paid on Bills & Payments (past ones you add later don't, since this already includes them)."}
        </p>

        {!isAccount && (
          <Field label="Return % per year (optional)"><input style={input} type="number" inputMode="decimal" step="0.01" value={form.returnRate} onChange={set("returnRate")} placeholder="e.g. 12" /></Field>
        )}
        {form.category === "other" && (
          <Field label="Description"><input style={input} value={form.description} onChange={set("description")} placeholder="What is it?" /></Field>
        )}
        {repeats && (
          <p style={{ margin: "0 0 14px", fontSize: 12, color: tokens.textMuted }}>
            Each instalment is added to your expenses as Pending. It becomes Paid when a matching bank SMS is imported or you mark it paid.
          </p>
        )}

        {error && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{error}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          {editing && <Button type="button" kind="danger" onClick={() => { data.remove("assets", asset.id).then(data.reload); onClose(); }}>Delete</Button>}
        </div>
      </form>
    </Sheet>
  );
};

// The amount shown for a holding: an account's balance, or what has been invested
// (the server adds each instalment to it once it's marked paid).
const assetAmount = (a, money) => (a.category === "account" || a.value > 0 ? money(a.value) : "—");

// With a return %, what the invested amount is estimated to be worth by now.
// Null without one.
const gainOf = (a, bills) => {
  const estimate = estimatedValue(a, bills);
  if (estimate === null || !(a.value > 0)) return null;
  return { estimate, gain: estimate - a.value };
};

const assetNote = (a, money, bills) => {
  const parts = [a.type];
  if (a.repeatUnit && a.repeatUnit !== "once" && a.amount > 0) parts.push(`${money(a.amount)} ${scheduleLabel(a.repeatUnit, a.repeatEvery).toLowerCase()}`);
  const g = gainOf(a, bills);
  if (g) parts.push(`worth about ${money(Math.round(g.estimate))} at ${a.returnRate}% (${g.gain >= 0 ? "+" : "-"}${money(Math.round(Math.abs(g.gain)))})`);
  else if (a.returnRate != null) parts.push(`${a.returnRate}% return`);
  if (a.description) parts.push(a.description);
  return parts.join(" · ");
};

// ---- income -------------------------------------------------------------------------

// Money coming in, like a salary. A repeating one shows the next expected payment;
// once it is received (marked here, or matched from a bank SMS) it counts in
// "Money in" on Home and the next one is lined up.
const IncomeSheet = ({ entry, data, onClose }) => {
  const input = useInputStyle();
  const { tokens } = useTheme();
  const editing = !!entry;
  const [form, setForm] = useState({
    source: entry ? entry.source : "",
    type: entry ? entry.type : "Salary",
    amount: entry ? String(entry.amount) : "",
    date: entry ? entry.date : todayIso(),
    repeatUnit: entry && entry.repeatUnit ? entry.repeatUnit : editing ? "once" : "months",
    repeatEvery: entry && entry.repeatEvery ? String(entry.repeatEvery) : "1",
    description: entry ? entry.description || "" : "",
    receivedInto: receivedIntoValue(entry),
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const hasAccounts = bankAccountsOf(data.assets).length > 0;
  const intoRequired = hasAccounts && sourceRequired(form.date, data.assets, data.cards);

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!form.source.trim()) return setError("Where does it come from?");
    if (Number.isNaN(amount) || amount < 0) return setError("Enter a valid amount.");
    if (intoRequired && !form.receivedInto) return setError("Choose the account it goes into.");
    setSaving(true);
    setError("");
    const body = {
      source: form.source.trim(),
      type: form.type,
      amount,
      date: form.date,
      repeatUnit: form.repeatUnit,
      repeatEvery: repeatEveryOf(form),
      description: form.description.trim(),
      receivedIntoId: receivedIntoId(form.receivedInto),
    };
    try {
      if (editing) await data.update("income", entry.id, body);
      else await data.create("income", { ...body, status: "Upcoming" });
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Sheet title={editing ? `Edit ${entry.source}` : "Add income"} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Source"><input style={input} value={form.source} onChange={set("source")} placeholder="e.g. Salary from Infosys" /></Field>
        <Field label="Type">
          <select style={input} value={form.type} onChange={set("type")}>{INCOME_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
        </Field>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}><Field label="Amount (₹)"><input style={input} type="number" inputMode="decimal" value={form.amount} onChange={set("amount")} placeholder="0" /></Field></div>
          <div style={{ flex: 1, minWidth: 0 }}><Field label="Expected on"><input style={input} type="date" value={form.date} onChange={set("date")} /></Field></div>
        </div>
        <FrequencyFields label="Repeats" form={form} setForm={setForm} />
        {hasAccounts && (
          <Field label="Received into">
            <MoneySourceSelect style={input} value={form.receivedInto} onChange={(v) => setForm((f) => ({ ...f, receivedInto: v }))} assets={data.assets} accountsOnly required={intoRequired} />
          </Field>
        )}
        <Field label="Description (optional)"><input style={input} value={form.description} onChange={set("description")} placeholder="A note for later" /></Field>
        {error && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{error}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          {editing && <Button type="button" kind="danger" onClick={() => { data.remove("income", entry.id); onClose(); }}>Delete</Button>}
        </div>
      </form>
    </Sheet>
  );
};

// Marking income received counts it in "Money in"; a repeating one gets its next
// payment added by the server, so reload to show it.
export const markIncomeReceived = (data, entry) =>
  data.update("income", entry.id, { status: "Received" }).then(() => data.reload());

const IncomePanel = ({ data }) => {
  const { tokens, fonts } = useTheme();
  const money = useMoney();
  const [editing, setEditing] = useState(null); // null closed, {} new, entry editing

  const upcoming = data.income.filter((i) => i.status !== "Received").sort((a, b) => (a.date < b.date ? -1 : 1));
  const receivedThisMonth = data.income.filter((i) => i.status === "Received" && isInPeriod(i.date, "Month")).reduce((s, i) => s + i.amount, 0);

  return (
    <>
      <SectionTitle right={<AddLink label="Add income" onClick={() => setEditing({})} />}>Income</SectionTitle>
      <Card style={{ padding: "4px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0", fontSize: 13, color: tokens.textMuted }}>
          <span>Received this month</span>
          <span style={{ fontFamily: fonts.mono, fontWeight: 700, color: tokens.positive }}>{money(receivedThisMonth)}</span>
        </div>
        {upcoming.length === 0 && <Empty>No income expected. Add your salary so it's counted when it arrives.</Empty>}
        {upcoming.map((i) => (
          <div key={i.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderTop: `1px solid ${tokens.surfaceBorder}` }}>
            <button onClick={() => setEditing(i)} style={{ flex: 1, minWidth: 0, background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer", color: tokens.textPrimary }}>
              <div style={{ fontSize: 15, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.source}</div>
              <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>
                {i.type} · expected {formatDate(i.date)}{i.repeatUnit && i.repeatUnit !== "once" ? ` · ${scheduleLabel(i.repeatUnit, i.repeatEvery).toLowerCase()}` : ""}
              </div>
            </button>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: tokens.positive }}>+{money(i.amount)}</div>
              {/* Upcoming until its date; from then on it waits to be marked received. */}
              {i.date > todayIso() ? (
                <div style={{ marginTop: 8 }}><StatusPill label="Upcoming" /></div>
              ) : (
                <button
                  onClick={() => markIncomeReceived(data, i)}
                  style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 999, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", backgroundColor: tokens.positiveBg, color: tokens.positive }}
                >
                  <CheckCircle size={13} strokeWidth={2.6} /> Mark as received
                </button>
              )}
            </div>
          </div>
        ))}
      </Card>
      {editing && <IncomeSheet entry={editing.id ? editing : null} data={data} onClose={() => setEditing(null)} />}
    </>
  );
};

// ---- loans ----------------------------------------------------------------------------

// A loan reduces net worth by its remaining balance. With an EMI date it also
// repeats: each EMI is added to expenses as Pending until paid.
const LoanSheet = ({ loan, data, onClose }) => {
  const input = useInputStyle();
  const { tokens } = useTheme();
  const editing = !!loan;
  const [form, setForm] = useState({
    name: loan ? loan.name : "",
    monthlyPayment: loan ? String(loan.monthlyPayment) : "",
    remainingBalance: loan ? String(loan.remainingBalance) : "",
    interestRate: loan && loan.interestRate != null ? String(loan.interestRate) : "",
    repeatUnit: loan && loan.repeatUnit && loan.repeatUnit !== "once" ? loan.repeatUnit : loan && !loan.startDate ? "once" : "months",
    repeatEvery: loan && loan.repeatEvery ? String(loan.repeatEvery) : "1",
    startDate: loan && loan.startDate ? loan.startDate : todayIso(),
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const repeats = form.repeatUnit !== "once";

  const submit = async (e) => {
    e.preventDefault();
    const monthlyPayment = parseFloat(form.monthlyPayment);
    const remainingBalance = parseFloat(form.remainingBalance);
    const interestRate = numberOrNull(form.interestRate);
    if (!form.name.trim()) return setError("Give the loan a name.");
    if (Number.isNaN(monthlyPayment) || monthlyPayment < 0) return setError("Enter a valid EMI amount.");
    if (Number.isNaN(remainingBalance) || remainingBalance < 0) return setError("Enter a valid remaining balance.");
    if (Number.isNaN(interestRate) || (interestRate !== null && (interestRate < 0 || interestRate > 100))) return setError("Enter the interest as a percentage, or leave it empty.");
    if (repeats && !form.startDate) return setError("Choose the date of the next EMI.");
    setSaving(true);
    setError("");
    try {
      const body = {
        name: form.name.trim(),
        monthlyPayment,
        remainingBalance,
        interestRate,
        repeatUnit: repeats ? form.repeatUnit : "once",
        repeatEvery: repeatEveryOf(form),
        startDate: repeats ? form.startDate : null,
      };
      if (editing) await data.update("loans", loan.id, body);
      else await data.create("loans", body);
      data.reload();
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Sheet title={editing ? "Edit loan" : "Add loan"} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Name"><input style={input} value={form.name} onChange={set("name")} placeholder="e.g. Car Loan" /></Field>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}><Field label="EMI amount (₹)"><input style={input} type="number" inputMode="decimal" value={form.monthlyPayment} onChange={set("monthlyPayment")} placeholder="0" /></Field></div>
          <div style={{ flex: 1, minWidth: 0 }}><Field label="Interest % per year"><input style={input} type="number" inputMode="decimal" step="0.01" value={form.interestRate} onChange={set("interestRate")} placeholder="e.g. 9.5" /></Field></div>
        </div>
        <Field label="Remaining balance (₹)"><input style={input} type="number" inputMode="decimal" value={form.remainingBalance} onChange={set("remainingBalance")} placeholder="0" /></Field>
        <FrequencyFields label="EMI frequency" form={form} setForm={setForm} />
        {repeats && <Field label="Next EMI on"><input style={input} type="date" value={form.startDate} onChange={set("startDate")} /></Field>}
        {repeats && (
          <p style={{ margin: "0 0 14px", fontSize: 12, color: tokens.textMuted }}>
            Each EMI is added to your expenses as Pending, and you get a reminder until it's paid. When it's marked paid, the interest is worked out from the rate and the rest comes off the remaining balance. The remaining balance you type here already counts every EMI up to today.
          </p>
        )}
        {error && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{error}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          {editing && <Button type="button" kind="danger" onClick={() => { data.remove("loans", loan.id).then(data.reload); onClose(); }}>Delete</Button>}
        </div>
      </form>
    </Sheet>
  );
};

const loanNote = (l, money) => {
  const repeats = l.repeatUnit && l.repeatUnit !== "once";
  const parts = [repeats ? `${money(l.monthlyPayment)} ${scheduleLabel(l.repeatUnit, l.repeatEvery).toLowerCase()}` : `${money(l.monthlyPayment)} / month`];
  if (l.interestRate != null) parts.push(`${l.interestRate}% interest`);
  const payoff = payoffText(l, money);
  if (payoff) parts.push(payoff);
  return parts.join(" · ");
};

// ---- credit cards ------------------------------------------------------------------------

// A card: its limit, what's owed on it now, the day its bill is made, and optionally
// the last 4 digits (to match bank SMS). What's owed goes up with expenses paid from
// it, and down when its bill is paid.
const CardSheet = ({ card, data, onClose }) => {
  const input = useInputStyle();
  const { tokens } = useTheme();
  const editing = !!card;
  const [form, setForm] = useState({
    name: card ? card.name : "",
    cardLimit: card ? String(card.cardLimit) : "",
    balance: card ? String(card.balance) : "",
    billingDay: card ? String(card.billingDay) : "1",
    last4: card && card.last4 ? card.last4 : "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const cardLimit = parseFloat(form.cardLimit);
    const balance = form.balance.trim() === "" ? 0 : parseFloat(form.balance);
    if (!form.name.trim()) return setError("Give the card a name.");
    if (Number.isNaN(cardLimit) || cardLimit < 0) return setError("Enter the card's limit.");
    if (Number.isNaN(balance) || balance < 0) return setError("Enter what's owed on it now, or leave it empty.");
    if (form.last4.trim() && !/^\d{4}$/.test(form.last4.trim())) return setError("Enter the last 4 digits of the card, or leave it empty.");
    setSaving(true);
    setError("");
    const body = { name: form.name.trim(), cardLimit, balance, billingDay: parseInt(form.billingDay, 10), last4: form.last4.trim() || null };
    try {
      if (editing) await data.update("cards", card.id, body);
      else await data.create("cards", body);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Sheet title={editing ? `Edit ${card.name}` : "Add credit card"} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Name"><input style={input} value={form.name} onChange={set("name")} placeholder="e.g. HDFC Millennia" /></Field>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}><Field label="Limit (₹)"><input style={input} type="number" inputMode="decimal" value={form.cardLimit} onChange={set("cardLimit")} placeholder="0" /></Field></div>
          <div style={{ flex: 1, minWidth: 0 }}><Field label="Owed now (₹)"><input style={input} type="number" inputMode="decimal" value={form.balance} onChange={set("balance")} placeholder="0" /></Field></div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Field label="Bill day">
              <select style={input} value={form.billingDay} onChange={set("billingDay")}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}><Field label="Last 4 digits (optional)"><input style={input} inputMode="numeric" maxLength={4} value={form.last4} onChange={set("last4")} placeholder="e.g. 9876" /></Field></div>
        </div>
        <p style={{ margin: "0 0 14px", fontSize: 12, color: tokens.textMuted }}>
          Expenses paid from this card add to what's owed on it; paying its bill takes it off. With the last 4 digits, bank SMS about this card are matched to it.
        </p>
        {error && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{error}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          {editing && !confirmDelete && <Button type="button" kind="danger" onClick={() => setConfirmDelete(true)}>Delete card</Button>}
          {editing && confirmDelete && <Button type="button" kind="danger" onClick={() => { data.remove("cards", card.id); onClose(); }}>Tap again to delete</Button>}
        </div>
      </form>
    </Sheet>
  );
};

// Paying a card's bill from a bank account: what's owed on the card and the account's
// balance both go down. It's kept as a "Card payment" in Activity (not counted as
// spending); deleting it there undoes it.
const PayCardSheet = ({ card, data, onClose }) => {
  const input = useInputStyle();
  const { tokens } = useTheme();
  const money = useMoney();
  const [amount, setAmount] = useState(card.balance > 0 ? String(card.balance) : "");
  const [from, setFrom] = useState("");
  const [date, setDate] = useState(todayIso());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const hasAccounts = bankAccountsOf(data.assets).length > 0;

  const submit = async (e) => {
    e.preventDefault();
    const n = parseFloat(amount);
    if (Number.isNaN(n) || n <= 0) return setError("Enter how much you paid.");
    if (hasAccounts && !from) return setError("Choose the account you paid from.");
    setSaving(true);
    setError("");
    try {
      await data.payCard(card.id, { amount: n, fromAccountId: paidFromFields(from).paidFromId, date });
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Sheet title={`Pay ${card.name} bill`} onClose={onClose}>
      <form onSubmit={submit}>
        <p style={{ margin: "0 0 14px", fontSize: 13, color: tokens.textMuted }}>Owed now: {money(card.balance)}</p>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}><Field label="Amount paid (₹)"><input style={input} type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field></div>
          <div style={{ flex: 1, minWidth: 0 }}><Field label="Paid on"><input style={input} type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} /></Field></div>
        </div>
        {hasAccounts && (
          <Field label="Paid from">
            <MoneySourceSelect style={input} value={from} onChange={setFrom} assets={data.assets} accountsOnly required />
          </Field>
        )}
        {error && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{error}</div>}
        <Button type="submit" disabled={saving} style={{ width: "100%" }}>{saving ? "Saving…" : "Save payment"}</Button>
      </form>
    </Sheet>
  );
};

const CardsPanel = ({ data }) => {
  const { tokens, fonts } = useTheme();
  const money = useMoney();
  const [editing, setEditing] = useState(null); // null closed, {} new, card editing
  const [paying, setPaying] = useState(null);

  return (
    <>
      <SectionTitle right={<AddLink label="Add credit card" onClick={() => setEditing({})} />}>Credit cards</SectionTitle>
      <Card style={{ padding: "4px 16px" }}>
        {data.cards.length === 0 && <Empty>No cards yet. Add one to track what you owe on it and pay expenses from it.</Empty>}
        {data.cards.map((c, i) => {
          const used = c.cardLimit > 0 ? Math.min(100, (c.balance / c.cardLimit) * 100) : 0;
          return (
            <div key={c.id} style={{ padding: "13px 0", borderTop: i ? `1px solid ${tokens.surfaceBorder}` : "none" }}>
              <button onClick={() => setEditing(c)} style={{ width: "100%", display: "flex", justifyContent: "space-between", gap: 12, padding: 0, background: "none", border: "none", textAlign: "left", cursor: "pointer", color: "inherit" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: tokens.textPrimary }}>{c.name}{c.last4 ? ` ••${c.last4}` : ""}</div>
                  <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>{money(c.cardLimit - c.balance)} available of {money(c.cardLimit)} · bill day {c.billingDay}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: tokens.textPrimary, whiteSpace: "nowrap" }}>{money(c.balance)}</div>
                  <div style={{ fontSize: 11, color: tokens.textMuted }}>owed</div>
                </div>
              </button>
              <div style={{ height: 6, borderRadius: 3, backgroundColor: tokens.neutralSoft, margin: "8px 0", overflow: "hidden" }}>
                <div style={{ width: `${used}%`, height: "100%", backgroundColor: used > 70 ? tokens.negative : tokens.accent }} />
              </div>
              {c.balance > 0 && (
                <button
                  onClick={() => setPaying(c)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 999, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, backgroundColor: tokens.positiveBg, color: tokens.positive }}
                >
                  <CheckCircle size={13} strokeWidth={2.6} /> Pay bill
                </button>
              )}
            </div>
          );
        })}
      </Card>
      {editing && <CardSheet card={editing.id ? editing : null} data={data} onClose={() => setEditing(null)} />}
      {paying && <PayCardSheet card={paying} data={data} onClose={() => setPaying(null)} />}
    </>
  );
};

// ---- screen -------------------------------------------------------------------------

const AddLink = ({ label, onClick }) => (
  <LinkButton icon={Plus} label={label} onClick={onClick}>Add</LinkButton>
);

// Everything you own, earn and owe: accounts & investments, income, and loans.
// Net worth on Home is holdings minus loans.
const AccountsScreen = ({ data }) => {
  const { tokens, fonts } = useTheme();
  const money = useMoney();
  const [editingAsset, setEditingAsset] = useState(null); // null closed, {} = new, asset = editing
  const [editingLoan, setEditingLoan] = useState(null);
  const [editingBudget, setEditingBudget] = useState(null);

  // Across all investments and "other" holdings: what went in, and - for the ones
  // with a return % - the estimated gain on it.
  const investTotals = data.assets
    .filter((a) => a.category !== "account")
    .reduce(
      (t, a) => {
        const g = gainOf(a, data.bills);
        return { invested: t.invested + a.value, gain: t.gain + (g ? g.gain : 0), withValue: t.withValue + (g ? 1 : 0) };
      },
      { invested: 0, gain: 0, withValue: 0 }
    );

  const row = (key, first, onClick, title, note, amount) => (
    <button
      key={key}
      onClick={onClick}
      style={{ width: "100%", display: "flex", justifyContent: "space-between", gap: 12, padding: "13px 0", background: "none", border: "none", borderTop: first ? "none" : `1px solid ${tokens.surfaceBorder}`, textAlign: "left", cursor: "pointer", color: "inherit" }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: tokens.textPrimary }}>{title}</div>
        <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>{note}</div>
      </div>
      <div style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: tokens.textPrimary, whiteSpace: "nowrap" }}>{amount}</div>
    </button>
  );

  return (
    <div>
      <SectionTitle right={<AddLink label="Add account or investment" onClick={() => setEditingAsset({})} />}>
        Accounts &amp; investments
      </SectionTitle>
      <Card style={{ padding: "4px 16px" }}>
        {data.assets.length === 0 && <Empty>Nothing tracked yet. This is what makes up your net worth on Home.</Empty>}
        {data.assets.map((a, i) => row(a.id, i === 0, () => setEditingAsset(a), a.name, assetNote(a, money, data.bills), assetAmount(a, money)))}
        {investTotals.invested > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "12px 0", borderTop: `1px solid ${tokens.surfaceBorder}`, fontSize: 13 }}>
            <span style={{ color: tokens.textMuted }}>Invested so far</span>
            <span style={{ fontFamily: fonts.mono, fontWeight: 700, color: tokens.textPrimary }}>
              {money(investTotals.invested)}
              {investTotals.withValue > 0 && (
                <span style={{ marginLeft: 8, color: investTotals.gain >= 0 ? tokens.positive : tokens.negative }}>
                  {investTotals.gain >= 0 ? "+" : "-"}{money(Math.round(Math.abs(investTotals.gain)))} est.
                </span>
              )}
            </span>
          </div>
        )}
      </Card>

      <CardsPanel data={data} />

      <IncomePanel data={data} />

      {/* Trips, gadgets…: how much each needs and how much is saved toward it. */}
      <SectionTitle right={<AddLink label="Add planned budget" onClick={() => setEditingBudget({})} />}>Planned budgets</SectionTitle>
      <PlannedBudgets budgets={data.budgets} title="Saving for" onOpen={setEditingBudget} />

      <SectionTitle right={<AddLink label="Add loan" onClick={() => setEditingLoan({})} />}>Loans</SectionTitle>
      <Card style={{ padding: "4px 16px" }}>
        {data.loans.length === 0 && <Empty>No loans tracked. These reduce your net worth on Home.</Empty>}
        {data.loans.map((l, i) => row(l.id, i === 0, () => setEditingLoan(l), l.name, loanNote(l, money), money(l.remainingBalance)))}
      </Card>

      {editingAsset && <AssetSheet asset={editingAsset.id ? editingAsset : null} data={data} onClose={() => setEditingAsset(null)} />}
      {editingLoan && <LoanSheet loan={editingLoan.id ? editingLoan : null} data={data} onClose={() => setEditingLoan(null)} />}
      {editingBudget && <PlannedBudgetSheet budget={editingBudget.id ? editingBudget : null} data={data} onClose={() => setEditingBudget(null)} />}
    </div>
  );
};

export default AccountsScreen;
